/**
 * CrossEntityTopologyPage - Cross-Entity Topology Map (EQ-PROV-007)
 *
 * Force-directed graph of companies (nodes) and dispatch policies (edges)
 * within a Client_ID. Provides visual evidence for Patent Claims F and G:
 *   - Claim F: Four-tier scope hierarchy (Entity, Cross-Entity, Client, Platform)
 *   - Claim G: Cross-entity dispatch governance with policy-based routing
 *
 * Uses custom SVG spring-force simulation (no external graph libraries).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useStatus } from '../../contexts/StatusProvider';
import { Make_Authorized_API_Call } from '@equilateral/ui-shared';
import { API_ENDPOINTS } from '../../services/api/endpoints';

// ---------------------------------------------------------------------------
// Audit types
// ---------------------------------------------------------------------------

interface AuditSummary {
  total_dispatches: number;
  successful_dispatches: number;
  failed_dispatches: number;
  held_dispatches: number;
  period_start: string;
  period_end: string;
}

interface AuditEvent {
  audit_id: string;
  event_type: string;
  origin_entity_name: string;
  target_entity_name: string;
  consequence_tier: number;
  result: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TopologyNode {
  company_id: string;
  company_name: string;
  entity_count: number;
  agent_count: number;
  health_score: number;
  // Layout fields (populated by simulation)
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface TopologyEdge {
  origin_company_id: string;
  target_company_id: string;
  policy_name: string;
  tier_ceiling: string;
  permitted_categories: string[];
  total_dispatches: number;
  success_rate: number;
  bidirectional: boolean;
}

interface TopologyData {
  clientId: string;
  nodes: TopologyNode[];
  edges: TopologyEdge[];
  nodeCount: number;
  edgeCount: number;
}

type SidebarSelection =
  | { kind: 'node'; node: TopologyNode }
  | { kind: 'edge'; edge: TopologyEdge };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SVG_WIDTH = 900;
const SVG_HEIGHT = 620;
const CENTER_X = SVG_WIDTH / 2;
const CENTER_Y = SVG_HEIGHT / 2;

const MIN_NODE_RADIUS = 16;
const MAX_NODE_RADIUS = 40;
const MIN_EDGE_WIDTH = 1.5;
const MAX_EDGE_WIDTH = 8;

// Scope-elevation ring radii and labels (Claim F)
const SCOPE_RINGS = [
  { radius: 80, label: 'Entity', color: 'rgba(16,185,129,0.08)' },
  { radius: 180, label: 'Cross-Entity', color: 'rgba(59,130,246,0.06)' },
  { radius: 270, label: 'Client', color: 'rgba(139,92,246,0.05)' },
  { radius: 360, label: 'Platform', color: 'rgba(239,68,68,0.04)' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function healthColor(score: number): string {
  if (score > 0.8) return '#22c55e';
  if (score > 0.5) return '#eab308';
  return '#ef4444';
}

function edgeColor(successRate: number): string {
  if (successRate > 0.9) return '#22c55e';
  if (successRate > 0.7) return '#eab308';
  return '#ef4444';
}

function nodeRadius(agentCount: number, maxAgents: number): number {
  if (maxAgents <= 0) return MIN_NODE_RADIUS;
  const t = Math.min(agentCount / maxAgents, 1);
  return MIN_NODE_RADIUS + t * (MAX_NODE_RADIUS - MIN_NODE_RADIUS);
}

function edgeWidth(totalDispatches: number, maxDispatches: number): number {
  if (maxDispatches <= 0) return MIN_EDGE_WIDTH;
  const t = Math.min(totalDispatches / maxDispatches, 1);
  return MIN_EDGE_WIDTH + t * (MAX_EDGE_WIDTH - MIN_EDGE_WIDTH);
}

// ---------------------------------------------------------------------------
// Force-directed layout (mirrors TopologyViewerPage spring simulation)
// ---------------------------------------------------------------------------

function runForceSimulation(nodes: TopologyNode[], edges: TopologyEdge[]): void {
  // Initialise positions in a circle
  nodes.forEach((node, i) => {
    node.x = CENTER_X + Math.cos((i / nodes.length) * 2 * Math.PI) * 200;
    node.y = CENTER_Y + Math.sin((i / nodes.length) * 2 * Math.PI) * 200;
    node.vx = 0;
    node.vy = 0;
  });

  const iterations = 50;
  const springStrength = 0.01;
  const repulsionStrength = 500;
  const centeringStrength = 0.001;
  const damping = 0.9;

  for (let iter = 0; iter < iterations; iter++) {
    // Spring forces (edges)
    edges.forEach((edge) => {
      const source = nodes.find((n) => n.company_id === edge.origin_company_id);
      const target = nodes.find((n) => n.company_id === edge.target_company_id);
      if (!source || !target) return;

      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = (dist - 150) * springStrength;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;

      source.vx += fx;
      source.vy += fy;
      target.vx -= fx;
      target.vy -= fy;
    });

    // Repulsion forces (all node pairs)
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i];
        const b = nodes[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = repulsionStrength / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;

        a.vx -= fx;
        a.vy -= fy;
        b.vx += fx;
        b.vy += fy;
      }
    }

    // Centering force
    nodes.forEach((node) => {
      node.vx += (CENTER_X - node.x) * centeringStrength;
      node.vy += (CENTER_Y - node.y) * centeringStrength;
    });

    // Integrate positions & apply damping
    nodes.forEach((node) => {
      node.vx *= damping;
      node.vy *= damping;
      node.x += node.vx;
      node.y += node.vy;
    });
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CrossEntityTopologyPage: React.FC = () => {
  const { Client_ID } = useStatus();

  const [topology, setTopology] = useState<TopologyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<SidebarSelection | null>(null);
  const [showScopeOverlay, setShowScopeOverlay] = useState(false);

  // Audit state
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [showAuditEvents, setShowAuditEvents] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const svgRef = useRef<SVGSVGElement>(null);

  // -----------------------------------------------------------------------
  // Fetch topology data
  // -----------------------------------------------------------------------
  const fetchTopology = useCallback(async () => {
    if (!Client_ID) {
      setError('No client context available.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_TOPOLOGY}?Client_ID=${encodeURIComponent(Client_ID)}`;
      const response = await Make_Authorized_API_Call(url, 'GET');

      if (response?.success && response.data?.Records?.length) {
        const record = response.data.Records[0] as TopologyData;

        // Deep-clone nodes so we can mutate positions
        const nodes: TopologyNode[] = record.nodes.map((n) => ({ ...n, x: 0, y: 0, vx: 0, vy: 0 }));
        const edges: TopologyEdge[] = record.edges.map((e) => ({ ...e }));

        runForceSimulation(nodes, edges);

        setTopology({ ...record, nodes, edges });
      } else {
        setTopology(null);
        setError('No topology data returned.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch topology';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [Client_ID]);

  useEffect(() => {
    fetchTopology();
  }, [fetchTopology]);

  // -----------------------------------------------------------------------
  // Fetch audit summary
  // -----------------------------------------------------------------------
  const fetchAuditSummary = useCallback(async () => {
    if (!Client_ID) return;
    try {
      const url = `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_AUDIT_SUMMARY}?Client_ID=${encodeURIComponent(Client_ID)}`;
      const response = await Make_Authorized_API_Call(url, 'GET');
      if (response?.success && response.data?.Records?.length) {
        setAuditSummary(response.data.Records[0] as AuditSummary);
      }
    } catch (err) {
      console.error('Failed to fetch audit summary:', err);
    }
  }, [Client_ID]);

  const fetchAuditEvents = useCallback(async () => {
    if (!Client_ID) return;
    setAuditLoading(true);
    try {
      const url = `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_AUDIT_GET}?Client_ID=${encodeURIComponent(Client_ID)}&limit=10`;
      const response = await Make_Authorized_API_Call(url, 'GET');
      if (response?.success) {
        setAuditEvents(response.data?.Records || []);
      }
    } catch (err) {
      console.error('Failed to fetch audit events:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [Client_ID]);

  useEffect(() => {
    fetchAuditSummary();
  }, [fetchAuditSummary]);

  const handleViewAudit = () => {
    const next = !showAuditEvents;
    setShowAuditEvents(next);
    if (next && auditEvents.length === 0) {
      fetchAuditEvents();
    }
  };

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------
  const maxAgents = topology ? Math.max(...topology.nodes.map((n) => n.agent_count), 1) : 1;
  const maxDispatches = topology ? Math.max(...topology.edges.map((e) => e.total_dispatches), 1) : 1;
  const avgHealth = topology && topology.nodes.length > 0
    ? topology.nodes.reduce((sum, n) => sum + n.health_score, 0) / topology.nodes.length
    : 0;

  // -----------------------------------------------------------------------
  // Event handlers
  // -----------------------------------------------------------------------
  const handleNodeClick = (node: TopologyNode) => {
    setSelection({ kind: 'node', node });
  };

  const handleEdgeClick = (edge: TopologyEdge) => {
    setSelection({ kind: 'edge', edge });
  };

  const closeSidebar = () => {
    setSelection(null);
  };

  // -----------------------------------------------------------------------
  // Helpers for finding connected edges for sidebar
  // -----------------------------------------------------------------------
  const connectedEdges = (companyId: string): TopologyEdge[] => {
    if (!topology) return [];
    return topology.edges.filter(
      (e) => e.origin_company_id === companyId || e.target_company_id === companyId,
    );
  };

  const companyName = (companyId: string): string => {
    if (!topology) return companyId;
    return topology.nodes.find((n) => n.company_id === companyId)?.company_name ?? companyId;
  };

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  const renderScopeOverlay = () => {
    if (!showScopeOverlay) return null;
    return (
      <g>
        {[...SCOPE_RINGS].reverse().map((ring) => (
          <g key={ring.label}>
            <circle
              cx={CENTER_X}
              cy={CENTER_Y}
              r={ring.radius}
              fill={ring.color}
              stroke="rgba(100,100,100,0.15)"
              strokeWidth="1"
              strokeDasharray="4 4"
            />
            <text
              x={CENTER_X + ring.radius - 4}
              y={CENTER_Y - 6}
              fontSize="10"
              fill="#6b7280"
              textAnchor="end"
            >
              {ring.label}
            </text>
          </g>
        ))}
      </g>
    );
  };

  const renderEdges = () => {
    if (!topology) return null;

    return topology.edges.map((edge, idx) => {
      const source = topology.nodes.find((n) => n.company_id === edge.origin_company_id);
      const target = topology.nodes.find((n) => n.company_id === edge.target_company_id);
      if (!source || !target) return null;

      const color = edgeColor(edge.success_rate);
      const width = edgeWidth(edge.total_dispatches, maxDispatches);
      const isSelected =
        selection?.kind === 'edge' &&
        selection.edge.origin_company_id === edge.origin_company_id &&
        selection.edge.target_company_id === edge.target_company_id &&
        selection.edge.policy_name === edge.policy_name;

      const midX = (source.x + target.x) / 2;
      const midY = (source.y + target.y) / 2;

      const markerId = edge.bidirectional
        ? `arrow-bi-${idx}`
        : `arrow-${idx}`;

      return (
        <g
          key={`edge-${idx}`}
          onClick={(e) => { e.stopPropagation(); handleEdgeClick(edge); }}
          className="cursor-pointer"
        >
          {/* Arrowhead defs per-edge for colour */}
          <defs>
            <marker
              id={markerId}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
            </marker>
            {edge.bidirectional && (
              <marker
                id={`arrow-bi-start-${idx}`}
                viewBox="0 0 10 10"
                refX="2"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
              </marker>
            )}
          </defs>
          <line
            x1={source.x}
            y1={source.y}
            x2={target.x}
            y2={target.y}
            stroke={isSelected ? '#8b5cf6' : color}
            strokeWidth={isSelected ? width + 2 : width}
            strokeOpacity={0.7}
            markerEnd={`url(#${markerId})`}
            markerStart={edge.bidirectional ? `url(#arrow-bi-start-${idx})` : undefined}
          />
          {/* Invisible wider hit area */}
          <line
            x1={source.x}
            y1={source.y}
            x2={target.x}
            y2={target.y}
            stroke="transparent"
            strokeWidth={Math.max(width + 8, 14)}
          />
          {/* Tier label */}
          <text
            x={midX}
            y={midY - 6}
            fontSize="10"
            fontWeight="600"
            fill="#374151"
            textAnchor="middle"
          >
            {edge.tier_ceiling}
          </text>
        </g>
      );
    });
  };

  const renderNodes = () => {
    if (!topology) return null;

    return topology.nodes.map((node) => {
      const r = nodeRadius(node.agent_count, maxAgents);
      const fill = healthColor(node.health_score);
      const isSelected =
        selection?.kind === 'node' && selection.node.company_id === node.company_id;

      return (
        <g
          key={node.company_id}
          onClick={(e) => { e.stopPropagation(); handleNodeClick(node); }}
          className="cursor-pointer"
        >
          <circle
            cx={node.x}
            cy={node.y}
            r={isSelected ? r + 4 : r}
            fill={fill}
            stroke={isSelected ? '#8b5cf6' : '#ffffff'}
            strokeWidth={isSelected ? 3 : 2}
            opacity={0.9}
          />
          {/* Agent count in centre */}
          <text
            x={node.x}
            y={node.y + 4}
            fontSize="11"
            fontWeight="700"
            fill="#ffffff"
            textAnchor="middle"
          >
            {node.agent_count}
          </text>
          {/* Company name below */}
          <text
            x={node.x}
            y={node.y + r + 14}
            fontSize="11"
            fill="#374151"
            textAnchor="middle"
          >
            {node.company_name}
          </text>
        </g>
      );
    });
  };

  // -----------------------------------------------------------------------
  // Sidebar
  // -----------------------------------------------------------------------

  const renderSidebar = () => {
    if (!selection) return null;

    if (selection.kind === 'node') {
      const { node } = selection;
      const edges = connectedEdges(node.company_id);

      return (
        <div className="w-80 bg-white border-l border-gray-300 p-5 overflow-y-auto flex-shrink-0">
          <div className="flex justify-between items-center mb-4">
            <h3 className="m-0 text-lg">Company Details</h3>
            <button
              onClick={closeSidebar}
              className="px-2 py-1 bg-gray-200 border-none rounded cursor-pointer text-sm"
            >
              X
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <span className="font-semibold text-gray-500">Company Name</span>
              <p className="mt-0.5 mb-0">{node.company_name}</p>
            </div>
            <div>
              <span className="font-semibold text-gray-500">Company ID</span>
              <p className="mt-0.5 mb-0 font-mono text-xs">{node.company_id}</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-gray-50 rounded p-2 text-center">
                <div className="text-lg font-bold">{node.entity_count}</div>
                <div className="text-xs text-gray-500">Entities</div>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <div className="text-lg font-bold">{node.agent_count}</div>
                <div className="text-xs text-gray-500">Agents</div>
              </div>
              <div className="bg-gray-50 rounded p-2 text-center">
                <div
                  className="text-lg font-bold"
                  style={{ color: healthColor(node.health_score) }}
                >
                  {(node.health_score * 100).toFixed(0)}%
                </div>
                <div className="text-xs text-gray-500">Health</div>
              </div>
            </div>

            {edges.length > 0 && (
              <div>
                <span className="font-semibold text-gray-500">Connected Policies</span>
                <ul className="mt-1 mb-0 pl-4 space-y-1">
                  {edges.map((e, i) => {
                    const other =
                      e.origin_company_id === node.company_id
                        ? companyName(e.target_company_id)
                        : companyName(e.origin_company_id);
                    const direction =
                      e.origin_company_id === node.company_id ? 'to' : 'from';
                    return (
                      <li key={i} className="text-xs">
                        <span className="font-medium">{e.policy_name}</span>{' '}
                        <span className="text-gray-400">({direction} {other})</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Edge sidebar
    const { edge } = selection;
    return (
      <div className="w-80 bg-white border-l border-gray-300 p-5 overflow-y-auto flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <h3 className="m-0 text-lg">Policy Details</h3>
          <button
            onClick={closeSidebar}
            className="px-2 py-1 bg-gray-200 border-none rounded cursor-pointer text-sm"
          >
            X
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <span className="font-semibold text-gray-500">Policy Name</span>
            <p className="mt-0.5 mb-0 font-medium">{edge.policy_name}</p>
          </div>
          <div>
            <span className="font-semibold text-gray-500">Tier Ceiling</span>
            <p className="mt-0.5 mb-0">
              <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
                {edge.tier_ceiling}
              </span>
            </p>
          </div>
          <div>
            <span className="font-semibold text-gray-500">Route</span>
            <p className="mt-0.5 mb-0 text-xs">
              {companyName(edge.origin_company_id)} &rarr; {companyName(edge.target_company_id)}
              {edge.bidirectional && ' (bidirectional)'}
            </p>
          </div>
          <div>
            <span className="font-semibold text-gray-500">Permitted Categories</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {edge.permitted_categories.map((cat) => (
                <span
                  key={cat}
                  className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs"
                >
                  {cat}
                </span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-50 rounded p-2 text-center">
              <div className="text-lg font-bold">{edge.total_dispatches.toLocaleString()}</div>
              <div className="text-xs text-gray-500">Dispatches</div>
            </div>
            <div className="bg-gray-50 rounded p-2 text-center">
              <div
                className="text-lg font-bold"
                style={{ color: edgeColor(edge.success_rate) }}
              >
                {(edge.success_rate * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500">Success Rate</div>
            </div>
          </div>
          <div>
            <span className="font-semibold text-gray-500">Bidirectional</span>
            <p className="mt-0.5 mb-0">{edge.bidirectional ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </div>
    );
  };

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="p-4 bg-white border-b border-gray-300">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="m-0 text-xl">Cross-Entity Topology Map</h2>
              <p className="mt-1 mb-0 text-gray-500 text-sm">
                EQ-PROV-007 &mdash; Dispatch policies across companies
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showScopeOverlay}
                  onChange={(e) => setShowScopeOverlay(e.target.checked)}
                  className="accent-purple-600"
                />
                Scope Elevation Overlay
              </label>
              <button
                onClick={fetchTopology}
                className="px-3 py-1.5 bg-blue-500 text-white text-sm border-none rounded cursor-pointer hover:bg-blue-600"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Audit Summary Widget */}
        {auditSummary && (
          <div className="px-5 pt-4">
            <div className="flex items-center gap-6 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">
                  {auditSummary.total_dispatches.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Total Dispatches</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">
                  {auditSummary.successful_dispatches.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-red-600">
                  {auditSummary.failed_dispatches.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-yellow-600">
                  {auditSummary.held_dispatches.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500">Held</div>
              </div>
              <div className="ml-auto">
                <button
                  onClick={handleViewAudit}
                  className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                >
                  {showAuditEvents ? 'Hide Audit' : 'View Audit'}
                </button>
              </div>
            </div>

            {/* Audit Events Table */}
            {showAuditEvents && (
              <div className="mt-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
                {auditLoading ? (
                  <div className="p-4 text-center text-gray-500 text-sm">Loading audit events...</div>
                ) : auditEvents.length === 0 ? (
                  <div className="p-4 text-center text-gray-400 text-sm">No audit events found.</div>
                ) : (
                  <table className="w-full text-sm text-left text-gray-600">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-3 py-2">Time</th>
                        <th className="px-3 py-2">Event</th>
                        <th className="px-3 py-2">Origin</th>
                        <th className="px-3 py-2">Target</th>
                        <th className="px-3 py-2">Tier</th>
                        <th className="px-3 py-2">Result</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {auditEvents.map((evt) => (
                        <tr key={evt.audit_id}>
                          <td className="px-3 py-2 whitespace-nowrap text-xs">
                            {new Date(evt.created_at).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs">{evt.event_type}</td>
                          <td className="px-3 py-2 text-xs">{evt.origin_entity_name}</td>
                          <td className="px-3 py-2 text-xs">{evt.target_entity_name}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                evt.consequence_tier <= 2
                                  ? 'bg-green-100 text-green-800'
                                  : evt.consequence_tier <= 3
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}
                            >
                              T{evt.consequence_tier}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                evt.result === 'PASS'
                                  ? 'bg-green-100 text-green-800'
                                  : evt.result === 'HOLD'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {evt.result}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Canvas */}
        <div className="flex-1 overflow-auto p-5">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Loading topology...</p>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-red-600 font-medium">{error}</p>
                <button
                  onClick={fetchTopology}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white border-none rounded cursor-pointer text-sm"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && topology && (
            <svg
              ref={svgRef}
              width={SVG_WIDTH}
              height={SVG_HEIGHT}
              className="bg-white border-2 border-gray-300 rounded-lg mx-auto block"
              onClick={() => setSelection(null)}
            >
              {/* Scope elevation rings (behind everything) */}
              {renderScopeOverlay()}

              {/* Edges */}
              <g>{renderEdges()}</g>

              {/* Nodes (on top of edges) */}
              <g>{renderNodes()}</g>

              {/* Legend */}
              <g transform="translate(16, 16)">
                <text fontSize="10" fontWeight="600" fill="#6b7280">
                  Health:
                </text>
                <circle cx={48} cy={-3} r={5} fill="#22c55e" />
                <text x={56} fontSize="9" fill="#6b7280">&gt;80%</text>
                <circle cx={88} cy={-3} r={5} fill="#eab308" />
                <text x={96} fontSize="9" fill="#6b7280">&gt;50%</text>
                <circle cx={128} cy={-3} r={5} fill="#ef4444" />
                <text x={136} fontSize="9" fill="#6b7280">&lt;50%</text>
              </g>
            </svg>
          )}

          {!loading && !error && !topology && (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400">No topology data available for this client.</p>
            </div>
          )}
        </div>

        {/* Footer stats */}
        <div className="py-3 px-4 bg-white border-t border-gray-300 text-sm text-gray-500">
          {topology ? (
            <>
              {topology.nodeCount} nodes &middot; {topology.edgeCount} edges &middot; Avg health{' '}
              <span style={{ color: healthColor(avgHealth) }}>
                {(avgHealth * 100).toFixed(1)}%
              </span>
            </>
          ) : (
            'No data'
          )}
        </div>
      </div>

      {/* Right sidebar */}
      {renderSidebar()}
    </div>
  );
};
